<template>
    <div>
        <panel-competition-form-box>
            <div class="col-12">
                <div class="form__headline mab16">Zawody cykliczne</div>
                <p class="mab8">
                    Umieszczenie zawodów w cyklu pozwala uczestnikom na jednoczesną rejestrację we wszystkich zawodach w
                    danej serii. Opłata za udział może uwzględniać rabat za jednoczesną rejestrację w wielu zawodach z
                    cyklu.
                </p>
            </div>
        </panel-competition-form-box>

        <panel-competition-form-box class="mat16">
            <div class="col-12 col-lg-7">
                <div class="cycle-mode-option">
                    <panel-input-base-radio
                        v-model="form.mode"
                        name="cycle-mode"
                        :option="{ value: 'existing', title: 'Dodaj zawody do istniejącego cyklu' }"
                    />
                    <div v-if="form.mode === 'existing'" class="cycle-mode-option__field">
                        <p class="form-input__label">Wybierz istniejący cykl</p>
                        <base-input-select
                            v-model="form.selectedCycleId"
                            is-panel
                            placeholder="Wpisz nazwę cyklu"
                            label-select="name"
                            :options="cycleOptions"
                            :reduce="(item) => item.id"
                            :error="errors.id || errors.cycle"
                        />
                    </div>
                </div>

                <div class="cycle-mode-option">
                    <panel-input-base-radio
                        v-model="form.mode"
                        name="cycle-mode"
                        :option="{ value: 'create', title: 'Stwórz nowy cykl' }"
                    />
                    <div v-if="form.mode === 'create'" class="cycle-mode-option__field">
                        <panel-input-text
                            v-model="form.newCycleName"
                            label="Nazwa nowego cyklu"
                            hint="Wpisz nazwę cyklu"
                            :error.sync="errors.id"
                        />
                    </div>
                </div>
            </div>
        </panel-competition-form-box>

        <panel-competition-form-box v-if="isJoiningExistingCycle" class="mat16">
            <div class="col-12">
                <p class="form-input__hint" style="margin-top: 0">
                    Ustawienia rabatów należą do cyklu i wczytają się po zapisaniu przypisania. Edycja zniżek jest
                    wspólna dla wszystkich zawodów w serii.
                </p>
            </div>
        </panel-competition-form-box>

        <template v-if="hasCycleSettings">
            <panel-competition-form-box class="mat16">
                <div class="col-12 col-lg-7">
                    <p class="form-input__label mab16">Możliwość przenoszenia rejestracji po dokonaniu opłaty</p>
                    <panel-input-base-radio
                        :value="moveRegistrationValue"
                        name="move-registration"
                        :option="{ value: 'yes', title: 'Tak' }"
                        @input="setMoveRegistration"
                    />
                    <panel-input-base-radio
                        :value="moveRegistrationValue"
                        name="move-registration"
                        :option="{ value: 'no', title: 'Nie' }"
                        @input="setMoveRegistration"
                    />
                </div>
                <div class="col-12 col-lg-4">
                    <p class="form-input__hint">
                        Uczestnicy mogą przenieść swoją rejestrację do innych zawodów w cyklu nawet po opłaceniu startu.
                        Różnica w opłacie nie zostanie wyrównana.
                    </p>
                </div>
            </panel-competition-form-box>

            <panel-competition-form-box class="mat16">
                <div class="col-12">
                    <p class="form-input__label mab8">Rabaty dla opłat startowych w cyklu</p>
                </div>
                <div class="col-12 col-lg-7">
                    <div class="discount-headers mab8">
                        <span class="discount-headers__starts">Liczba startów od której zostanie naliczany rabat</span>
                        <span class="discount-headers__amount">Rabat zł</span>
                        <span class="discount-headers__trash" aria-hidden="true" />
                    </div>

                    <div
                        v-for="(row, index) in form.discountRows"
                        :key="`discount-${index}`"
                        class="discount-row"
                        :class="{ 'discount-row--error': isDuplicateStarts(index) }"
                    >
                        <div class="discount-stepper">
                            <button
                                type="button"
                                class="discount-stepper__btn"
                                :disabled="!canChangeStarts(index, -1)"
                                @click="changeStarts(index, -1)"
                            >
                                <i class="fas fa-minus" />
                            </button>
                            <input
                                v-model.number="row.starts"
                                class="discount-stepper__input"
                                type="number"
                                min="2"
                                @blur="normalizeStarts(index)"
                            />
                            <button
                                type="button"
                                class="discount-stepper__btn"
                                :disabled="!canChangeStarts(index, 1)"
                                @click="changeStarts(index, 1)"
                            >
                                <i class="fas fa-plus" />
                            </button>
                        </div>
                        <span class="discount-row__label">liczba startów</span>
                        <input
                            v-model="row.amount"
                            class="discount-row__amount"
                            type="text"
                            inputmode="decimal"
                            placeholder="0"
                        />
                        <button
                            type="button"
                            class="discount-row__trash"
                            title="Usuń próg"
                            @click="removeDiscountRow(index)"
                        >
                            <i class="fas fa-trash" />
                        </button>
                    </div>

                    <p v-if="hasDuplicateStarts" class="discount-error mat8">
                        Każdy próg musi mieć unikalną liczbę startów.
                    </p>
                    <p v-if="isDiscountLimitReached" class="discount-error mat8">
                        Możesz dodać maksymalnie {{ maxDiscounts }} progów rabatowych.
                    </p>

                    <div class="discount-add mat8">
                        <base-button btn2 short :disabled="isDiscountLimitReached" @click="addDiscountRow">
                            Dodaj nowy próg
                        </base-button>
                    </div>
                </div>
                <div class="col-12 col-lg-4">
                    <p class="form-input__hint">
                        Ustal progi liczby startów i odpowiadające im kwoty rabatów do opłaty startowej.<br /><br />
                        Wpisana kwota rabatu zmniejsza każdą opłatę startową w cyklu, nie sumę wszystkich opłat.
                        Rabaty są wspólne dla całego cyklu — maksymalnie {{ maxDiscounts }} progów.
                    </p>
                </div>
            </panel-competition-form-box>

            <panel-competition-form-box class="mat16">
                <div class="col-12 col-lg-7">
                    <p class="form-input__label">
                        Data zakończenia uwzględniania rabatów
                        <span class="form-input__label--positive">(opcjonalnie)</span>
                    </p>
                    <base-input-date
                        v-model="form.discountEndedDate"
                        class="form-input__date mat8"
                        is-short
                        border
                        hint="Wybierz"
                        placeholder="DD.MM.RRRR"
                        :error="errors.discountEndedDate"
                    />
                </div>
                <div class="col-12 col-lg-4">
                    <p class="form-input__hint">Nie wybieraj daty aby rabaty były dostępne cały czas.</p>
                </div>
            </panel-competition-form-box>
        </template>

        <div class="col-lg-12 mat16 mab64">
            <div
                class="row"
                :class="assignedCycleId ? 'justify-content-between' : 'justify-content-end'"
            >
                <div v-if="assignedCycleId" class="col-12 col-lg-4 cycle-actions__detach">
                    <base-button btn2 :loading="loading" :disabled="loading" @click="$emit('detach')">
                        Odepnij zawody od cyklu
                    </base-button>
                </div>
                <div class="col-12 col-lg-4">
                    <base-button
                        btn1
                        :loading="loading"
                        :disabled="loading || hasDuplicateStarts || !canSave"
                        @click="$emit('save')"
                    >
                        Zapisz
                    </base-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import PanelCompetitionFormBox from '@/components/PanelCompetitionFormBox';
    import PanelInputBaseRadio from '@/components/PanelInputBaseRadio';
    import PanelInputText from '@/components/PanelInputText';

    export default {
        name: 'PanelCompetitionCycleForm',
        components: {
            PanelCompetitionFormBox,
            PanelInputBaseRadio,
            PanelInputText,
        },
        props: {
            value: {
                type: Object,
                required: true,
            },
            availableCycles: {
                type: Array,
                default: () => [],
            },
            assignedCycleId: {
                type: [Number, String],
                default: null,
            },
            maxDiscounts: {
                type: Number,
                default: 20,
            },
            errors: {
                type: Object,
                default: () => ({}),
            },
            loading: {
                type: Boolean,
                default: false,
            },
        },
        computed: {
            form: {
                get() {
                    return this.value;
                },
                set(next) {
                    this.$emit('input', next);
                },
            },
            cycleOptions() {
                return (this.availableCycles || []).map((cycle) => ({
                    id: cycle.id,
                    name: cycle.name || cycle.short || `Cykl #${cycle.id}`,
                }));
            },
            moveRegistrationValue() {
                return this.form.moveRegistrationOn ? 'yes' : 'no';
            },
            hasSelectedExistingCycle() {
                return this.form.selectedCycleId != null && this.form.selectedCycleId !== '';
            },
            isAssignedCycleSelected() {
                return (
                    this.hasSelectedExistingCycle &&
                    this.assignedCycleId != null &&
                    Number(this.form.selectedCycleId) === Number(this.assignedCycleId)
                );
            },
            isJoiningExistingCycle() {
                return this.form.mode === 'existing' && this.hasSelectedExistingCycle && !this.isAssignedCycleSelected;
            },
            hasCycleSettings() {
                if (this.form.mode === 'create') {
                    return Boolean((this.form.newCycleName || '').trim());
                }
                return this.isAssignedCycleSelected;
            },
            hasDuplicateStarts() {
                const starts = (this.form.discountRows || []).map((row) => Number(row.starts));
                return starts.some((value, index) => starts.indexOf(value) !== index);
            },
            isDiscountLimitReached() {
                return (this.form.discountRows || []).length >= this.maxDiscounts;
            },
            canSave() {
                if (this.form.mode === 'create') {
                    return Boolean((this.form.newCycleName || '').trim());
                }
                return this.hasSelectedExistingCycle;
            },
        },
        methods: {
            setMoveRegistration(value) {
                this.form.moveRegistrationOn = value === 'yes' || value === true || value === 1 || value === '1';
            },
            usedStarts(exceptIndex = -1) {
                const used = new Set();
                (this.form.discountRows || []).forEach((row, index) => {
                    if (index === exceptIndex) {
                        return;
                    }
                    used.add(Number(row.starts));
                });
                return used;
            },
            isDuplicateStarts(index) {
                const row = this.form.discountRows[index];
                if (!row) {
                    return false;
                }
                return this.usedStarts(index).has(Number(row.starts));
            },
            nextFreeStarts(from = 2, exceptIndex = -1) {
                const used = this.usedStarts(exceptIndex);
                let starts = Math.max(2, from);
                while (used.has(starts)) {
                    starts += 1;
                }
                return starts;
            },
            addDiscountRow() {
                if (this.isDiscountLimitReached) {
                    return;
                }
                this.form.discountRows.push({
                    starts: this.nextFreeStarts(2),
                    amount: '',
                });
            },
            removeDiscountRow(index) {
                this.form.discountRows.splice(index, 1);
            },
            canChangeStarts(index, delta) {
                const row = this.form.discountRows[index];
                if (!row) {
                    return false;
                }
                const candidate = Number(row.starts || 2) + delta;
                if (candidate < 2) {
                    return false;
                }
                return !this.usedStarts(index).has(candidate);
            },
            changeStarts(index, delta) {
                if (!this.canChangeStarts(index, delta)) {
                    return;
                }
                const row = this.form.discountRows[index];
                this.$set(row, 'starts', Number(row.starts || 2) + delta);
            },
            normalizeStarts(index) {
                const row = this.form.discountRows[index];
                if (!row) {
                    return;
                }
                const starts = Number(row.starts);
                const normalized = Number.isFinite(starts) && starts >= 2 ? Math.floor(starts) : 2;
                if (this.usedStarts(index).has(normalized)) {
                    this.$set(row, 'starts', this.nextFreeStarts(normalized, index));
                    return;
                }
                this.$set(row, 'starts', normalized);
            },
        },
    };
</script>

<style scoped>
    .form__headline {
        font-size: 16px;
        font-weight: 600;
        color: var(--color-white);
    }

    .form-input__hint {
        line-height: 21px;
        margin-top: 16px;
    }

    .form-input__label {
        margin-bottom: 8px;
    }

    .form-input__label--positive {
        color: var(--color-light-sage);
    }

    .form-input__date {
        color: black;
    }

    .cycle-mode-option + .cycle-mode-option {
        margin-top: 16px;
    }

    .cycle-mode-option__field {
        padding-left: 24px;
        margin-top: 12px;
    }

    .cycle-actions__detach {
        margin-bottom: 16px;
    }

    @media (min-width: 992px) {
        .cycle-actions__detach {
            margin-bottom: 0;
        }
    }

    .discount-headers,
    .discount-row {
        display: grid;
        grid-template-columns: 133px minmax(0, 1fr) 69px 40px;
        gap: 16px;
        align-items: center;
    }

    .discount-headers {
        padding: 0 8px;
        font-size: 14px;
        color: var(--color-white);
    }

    .discount-headers__starts {
        grid-column: 1 / 3;
    }

    .discount-headers__amount {
        grid-column: 3;
        text-align: left;
    }

    .discount-row {
        padding: 8px;
        margin-bottom: 8px;
        border-radius: 4px;
        background: var(--color-slate-two, #546e7a);
    }

    .discount-row--error {
        box-shadow: inset 0 0 0 1px #ff1744;
    }

    .discount-stepper {
        display: flex;
        align-items: center;
        flex-shrink: 0;
    }

    .discount-stepper__btn {
        width: 40px;
        height: 40px;
        border: 0;
        border-radius: 4px;
        background: #78909c;
        color: #fff;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .discount-stepper__btn:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }

    .discount-stepper__btn:first-child {
        border-radius: 4px 0 0 4px;
        background: #546e7a;
    }

    .discount-stepper__btn:last-child {
        border-radius: 0 4px 4px 0;
    }

    .discount-stepper__input {
        width: 53px;
        height: 40px;
        border: 0;
        text-align: center;
        font-size: 14px;
        color: #455a64;
        background: #fff;
    }

    .discount-row__label {
        font-size: 14px;
        color: var(--color-white);
    }

    .discount-row__amount {
        width: 69px;
        height: 40px;
        border: 0;
        border-radius: 4px;
        padding: 0 12px;
        font-size: 14px;
        color: #455a64;
        background: #fff;
        box-shadow: 0 8px 6px rgba(0, 0, 0, 0.1);
        box-sizing: border-box;
    }

    .discount-row__trash {
        width: 40px;
        height: 40px;
        border: 0;
        border-radius: 4px;
        background: transparent;
        color: #fff;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    }

    .discount-error {
        color: #ff8a80;
        font-size: 14px;
    }

    .discount-add {
        padding: 8px;
        border-radius: 4px;
        background: var(--color-slate-two, #546e7a);
    }

    @media (max-width: 767px) {
        .discount-headers,
        .discount-row {
            grid-template-columns: 133px 69px 40px;
            grid-template-areas:
                'starts amount trash'
                'label label label';
        }

        .discount-headers__starts {
            grid-column: 1 / -1;
        }

        .discount-headers__amount {
            display: none;
        }

        .discount-row__label {
            grid-column: 1 / -1;
        }
    }
</style>
